import {
  AccessibleOutlined,
  ApartmentOutlined,
  EmailOutlined,
  HomeOutlined,
  Phone,
  SecurityOutlined,
  WcOutlined,
} from '@mui/icons-material';
import { Box, Grid, Paper, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import CustomButton from '../../common/button/commonButton';
import HospitalDialog from '../../common/dialog/commonDialogBox';
import ReusableFormFields from '../../common/textfield/ReusableTextfield';
import { styles } from '../../commonStyle/formStyles';
import { fetchBlocks } from '../../redux/block/blockAction';
import { fetchDepartments } from '../../redux/department/departmentAction';
import { fetchFloors } from '../../redux/floor/floorAction';
import { fetchRoles } from '../../redux/role/roleAction';
import {
  fetchStaffMember,
  resetCurrentStaff,
  updateStaff,
} from '../../redux/staff/staffAction';
import { fetchWards } from '../../redux/ward/wardAction';
import { APP_LINK } from '../../common/menu/menuItems/menuList';
function StaffProfile() {
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
    setError,
    clearErrors,
    control,
  } = useForm();

  const dispatch = useDispatch();
  const [currentPage, setcurrentPage] = useState(1);
  const navigate = useNavigate();
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMessage, setDialogMessage] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);
  const [specificErrors, setSpecificErrors] = useState({
    email: null,
    phone: null,
  });

  const watchBlock = watch('block_id');
  const watchWard = watch('ward_id');
  const watchFloor = watch('floor_id');
  const staffStates = useSelector((state) => state?.staffs?.fetchStaff || {});
  const blockList = useSelector((state) => state?.block?.fetchBlocks || {});
  const floorList = useSelector((state) => state?.floor?.fetchFloors || {});
  const wardList = useSelector((state) => state?.ward?.fetchWards || {});
  const roleState = useSelector((state) => state?.role?.fetchRoles || {});
  const staffState = useSelector((state) => state?.staffs?.updateStaff);
  const user = useSelector((state) => state?.values?.loginValues?.[0] || {});

  useEffect(() => {
    dispatch(fetchRoles());
    dispatch(fetchDepartments());
    dispatch(fetchBlocks());
  }, [dispatch]);
  useEffect(() => {
    if (user.success) {
      dispatch(fetchStaffMember(user.user.staff_id));
    }
  }, [dispatch, user.user.staff_id]);
  useEffect(() => {
    const staffData = staffStates?.data?.data;
    if (staffData) {
      setValue('staff_id', staffData.staff_id || '');
      setValue('staff_name', staffData.staff_name || '');
      setValue('email', staffData.email || '');
      setValue('phone', staffData.phone || '');
      setValue('block_id', staffData.block_id || '');
      setValue('gender_type', staffData.gender_type || '');
      setValue('ward_id', staffData.ward_id || '');
      setValue('floor_id', staffData.floor_id || '');
      setValue('specialization_id', staffData.specialization_id || '');
      setValue('address', staffData.address || '');
      setValue('role_id', staffData.role_id || '');
      setValue('status', staffData?.User?.user_type || '');
      if (staffData.block_id) localStorage.setItem('block', staffData.block_id);
      if (staffData.ward_id) localStorage.setItem('ward', staffData.ward_id);
      if (staffData.floor_id) localStorage.setItem('floor', staffData.floor_id);
    }
  }, [staffStates, setValue]);
  useEffect(() => {
    if (watchBlock) {
      localStorage.setItem('block', watchBlock);
      dispatch(fetchFloors(watchBlock, currentPage));
    }
  }, [watchBlock, dispatch, currentPage]);
  useEffect(() => {
    if (watchBlock && watchFloor) {
      localStorage.setItem('floor', watchFloor);
      dispatch(fetchWards(watchBlock, watchFloor, currentPage));
    }
  }, [watchBlock, watchFloor, dispatch, currentPage]);
  useEffect(() => {
    if (staffState) {
      if (staffState.error === 'email must be unique') {
        setSpecificErrors((prev) => ({
          ...prev,
          email: 'Email already exists',
        }));
        setError('email', {
          type: 'manual',
          message: 'Email already exists',
        });
      } else if (staffState.error === 'phone must be unique') {
        setSpecificErrors((prev) => ({
          ...prev,
          phone: 'Phone number already exists',
        }));
        setError('phone', {
          type: 'manual',
          message: 'Phone number already exists',
        });
      } else if (staffState.success) {
        setIsSuccess(true);
        setOpenDialog(true);
        setDialogMessage('Staff updated successfully');
      } else if (!staffState.success && staffState.error) {
        setIsSuccess(false);
        setOpenDialog(true);
        setDialogMessage(staffState.errorMessage || 'An error occurred');
      }
      dispatch(resetCurrentStaff());
    }
  }, [staffState, setError, dispatch]);
  const inputFields = [
    {
      name: 'staff_id',
      label: 'Staff Id',
      placeholder: 'Enter Staff Id',
      type: 'text',
      rules: { required: 'Staff Id is required' },
      startIcon: <SecurityOutlined />,
    },
    {
      name: 'staff_name',
      label: 'Staff Name',
      placeholder: 'Enter name',
      type: 'text',
      rules: { required: 'Name is required' },
      startIcon: <SecurityOutlined />,
    },
    {
      name: 'email',
      label: 'Email',
      placeholder: 'Enter email',
      type: 'email',
      rules: { required: 'Email is required' },
      startIcon: <EmailOutlined />,
    },
    {
      name: 'phone',
      label: 'Phone Number',
      placeholder: 'Enter phone number',
      type: 'tel',
      maxLength: 10,
      showCharCount: false,
      rules: {
        required: 'Phone number is required',
        pattern: {
          value: /^(6|7|8|9)\d{9}$/,
          message: 'Invalid phone number',
        },
        maxLength: {
          value: 10,
          message: 'Phone number should be 10 digits',
        },
      },
      startIcon: <Phone />,
    },
    {
      name: 'address',
      label: 'Address',
      placeholder: 'Enter address',
      type: 'text',
      rules: { required: 'Address is required' },
      startIcon: <HomeOutlined />,
    },
    {
      name: 'gender_type',
      label: 'Gender',
      placeholder: 'Select gender',
      type: 'select',
      options: [
        { value: 'M', label: 'Male' },
        { value: 'F', label: 'Female' },
      ],
      rules: { required: 'Gender is required' },
      startIcon: <WcOutlined />,
    },

    {
      name: 'block_id',
      label: 'Block',
      placeholder: 'Enter block',
      type: 'select',
      readOnly: true,
      options:
        blockList?.Blocks?.data?.map((block) => ({
          value: block.id,
          label: block.block_name,
        })) || [],
      rules: { required: 'Block is required' },
      startIcon: <ApartmentOutlined />,
    },
    {
      name: 'floor_id',
      label: 'Floor',
      placeholder: 'Enter Floor',
      type: 'select',
      readOnly: true,
      options:
        floorList?.floors?.data?.map((floor) => ({
          value: floor.id,
          label: floor.floor_name,
        })) || [],
      rules: { required: 'Floor is required' },
      startIcon: <ApartmentOutlined />,
    },
    {
      name: 'ward_id',
      label: 'Ward Name',
      placeholder: 'Select ward name',
      type: 'select',
      readOnly: true,
      options:
        wardList?.wards?.data?.map((ward) => ({
          value: ward.id,
          label: ward.ward_name,
        })) || [],
      rules: { required: 'Ward name is required' },
      startIcon: <HomeOutlined />,
    },
    {
      name: 'role_id',
      label: 'Role',
      placeholder: 'Select Position',
      type: 'select',
      readOnly: true,
      options:
        roleState?.Roles?.data?.map((role) => ({
          value: role.id,
          label: role.name,
        })) || [],
      rules: { required: 'Position is required' },
      startIcon: <WcOutlined />,
    },
    {
      name: 'status',
      label: 'Status',
      placeholder: 'Select status',
      type: 'select',
      readOnly: true,
      options: [
        { value: 1, label: 'Active' },
        { value: 2, label: 'Inactive' },
        { value: 3, label: 'InProgress' },
      ],
      rules: { required: 'Status is required' },
      startIcon: <AccessibleOutlined />,
    },
  ];
  const onSubmit = (data) => {
    const formData = {
      address: data.address,
      email: data.email,
      phone: data.phone,
      staff_id: data.staff_id,
      ward_id: data.ward_id,
      floor_id: data.floor_id,
      staff_name: data.staff_name,
      block_id: data.block_id,
      gender_type: data.gender_type,
      role_id: data.role_id,
      status: data.status,
      pincode: '',
    };
    setSpecificErrors({
      email: null,
      phone: null,
    });
    clearErrors(['email', 'phone']);
    dispatch(updateStaff(user.user.staff_id, formData));
  };
  const handleCancel = () => {
    reset();
    setSpecificErrors({
      email: null,
      phone: null,
    });
  };
  const handleCloseDialog = () => {
    setOpenDialog(false);
    navigate(APP_LINK.STAFFPROFILE);
  };
  const handleBack = () => {
    navigate(-1);
  };
  return (
    <Box sx={styles.container}>
      <Paper sx={styles.paper} elevation={2}>
        <Grid container spacing={2} mb={3}>
          <Grid item xs={12} sm={12} md={10} lg={10} xl={10}>
            <Typography variant="h3" fontWeight={'bold'} sx={styles.title}>
              Staff Profile
            </Typography>
          </Grid>
          <Grid item xs={12} sm={12} md={2} lg={2} xl={2}>
            <Box sx={styles.backButtonContainer}>
              <CustomButton
                type="button"
                text="Back"
                variant="contained"
                disableElevation
                size="small"
                onClick={handleBack}
              />
            </Box>
          </Grid>
        </Grid>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={4}>
            <Grid item xs={12}>
              <ReusableFormFields
                inputFields={inputFields}
                register={register}
                errors={errors}
                setValue={setValue}
                control={control}
                gridProps={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}
              />
            </Grid>
          </Grid>
          <Box sx={styles.buttonsContainer}>
            <CustomButton
              type="button"
              text="Cancel"
              variant="contained"
              disableElevation
              size="small"
              onClick={handleCancel}
            />
            <CustomButton
              type="submit"
              text="Submit"
              variant="contained"
              disableElevation
              size="small"
              disabled={staffState?.inProgress}
              loading={staffState?.inProgress}
            />
          </Box>
        </form>
      </Paper>
      <HospitalDialog
        open={openDialog}
        onClose={handleCloseDialog}
        isSuccess={isSuccess}
        dialogMessage={dialogMessage}
        title={isSuccess ? 'Staff Profile' : 'Staff Profile Error'}
      />
    </Box>
  );
}
export default StaffProfile;
